// One source of truth for <title>, meta description and the shared JSON-LD
// pieces, used by BOTH the React <SEO> calls and scripts/generate-static-pages.js.
//
// Dependency-free CommonJS (like stateCost.js): webpack and plain node both load
// it. Data comes in as arguments; this file never imports the ESM data modules.
//
// Rules it enforces:
//   - a <title> is at most 60 characters. brandTitle() adds " | EVs for Idiots"
//     only when the result still fits.
//   - a meta description is 120 to 160 characters and never ends mid-word.

const { SITE, SITE_NAME, BY_HREF } = require('../config/siteRegistry');

const TITLE_MAX = 60;
const META_MIN = 120;
const META_MAX = 160;
const BRAND_SUFFIX = ` | ${SITE_NAME}`;

// First candidate that fits in 60 characters (or the shortest, if none do).
function fitTitle(candidates) {
  const list = candidates.filter(Boolean);
  const fit = list.find((t) => t.length <= TITLE_MAX);
  if (fit) return fit;
  return [...list].sort((a, b) => a.length - b.length)[0];
}

function brandTitle(base) {
  if (!base) return `Should You Buy an EV? Honest Answers | ${SITE_NAME}`;
  if (base.includes(SITE_NAME)) return base;
  const branded = base + BRAND_SUFFIX;
  return branded.length <= TITLE_MAX ? branded : base;
}

// Split after sentence-ending punctuation. No regex lookbehind on purpose:
// Safari before 16.4 cannot parse one (same trick as statementPages.js).
function splitSentences(text) {
  return String(text || '').replace(/([.!?])\s+/g, '$1\u0001').split('\u0001').filter(Boolean);
}

// Cut at a word boundary to at most max characters, without a dangling comma.
function trimAtWord(text, max) {
  const s = String(text || '').replace(/\s+/g, ' ').trim();
  if (s.length <= max) return s;
  const cut = s.slice(0, max + 1).replace(/\s+\S*$/, '');
  return cut.replace(/[\s,;:(]+$/, '');
}

const endSentence = (s) => (/[.!?]$/.test(s) ? s : `${s}.`);

// Meta description from a primary text plus optional extra sentences used to
// reach the 120-character floor. Prefers whole sentences; falls back to a word
// boundary with an ellipsis.
function fitMeta(primary, extras = [], max = META_MAX) {
  const base = String(primary || '').replace(/\s+/g, ' ').trim();
  let out = '';
  for (const sentence of splitSentences(base)) {
    const next = out ? `${out} ${sentence}` : sentence;
    if (next.length > max) break;
    out = next;
  }
  const cutWithEllipsis = () => {
    const t = trimAtWord(base, max - 3);
    return /[.!?]$/.test(t) ? t : `${t.replace(/[.\s]+$/, '')}...`;
  };
  if (!out) out = cutWithEllipsis();
  for (const extra of extras.filter(Boolean)) {
    if (out.length >= META_MIN) break;
    const next = `${endSentence(out)} ${extra}`;
    if (next.length <= max) out = next;
  }
  if (out.length < META_MIN && base.length > out.length) {
    // whole sentences were too short; take as much of the text as fits instead
    out = cutWithEllipsis();
  }
  return out;
}

// ---------- hubs (registry-driven) ----------
function hubTitle(href) {
  const p = BY_HREF[href];
  return p ? p.title : null;
}
function hubDescription(href) {
  const p = BY_HREF[href];
  return p ? p.description : null;
}

// ---------- shared JSON-LD ----------
const LOGO_URL = `${SITE}/logo512.png`;
const ORGANIZATION_ID = `${SITE}/#organization`;

function organizationJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': ORGANIZATION_ID,
    name: SITE_NAME,
    url: SITE,
    logo: { '@type': 'ImageObject', url: LOGO_URL, width: 512, height: 512 },
    parentOrganization: { '@type': 'Organization', name: 'ADAMAPPS, LLC', url: 'https://adamapps.co' },
  };
}

// Publisher/author block for Article-type schema (Google wants a logo on it).
function publisher() {
  return {
    '@type': 'Organization',
    '@id': ORGANIZATION_ID,
    name: SITE_NAME,
    url: SITE,
    logo: { '@type': 'ImageObject', url: LOGO_URL, width: 512, height: 512 },
  };
}

function breadcrumbJsonLd(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [{ name: 'Home', path: '' }, ...items].map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      item: `${SITE}${it.path}`,
    })),
  };
}

// ---------- EV model pages (/find-your-ev/:slug) ----------
const fmtUsd = (n) => '$' + Number(n).toLocaleString('en-US');
const evFullName = (ev) => `${ev.make} ${ev.model}`;
const rangeText = (ev) => (ev.range.min === ev.range.max ? `${ev.range.max}` : `${ev.range.min}-${ev.range.max}`);

// Query phrase first: people search "[model] price", "[model] range".
function evTitle(ev) {
  const n = evFullName(ev);
  return fitTitle([
    `${n} Price, Range and Specs (${ev.year})`,
    `${n} Price, Range, Specs (${ev.year})`,
    `${n} Price and Range (${ev.year})`,
    `${n} Price and Range`,
  ]);
}

function evDescription(ev) {
  const n = `${ev.year} ${evFullName(ev)}`;
  const est = ev.status === 'coming_soon' ? 'is expected to start' : 'starts';
  const charge = ev.dcFastCharge ? ` and ${ev.dcFastCharge} DC fast charging` : '';
  // Cars the EPA has not rated carry a rangeSource ('GM estimate', 'Rivian estimate', ...).
  const range = ev.rangeSource ? `miles of range (${ev.rangeSource})` : 'miles of EPA range';
  const lead = `The ${n} ${est} at ${fmtUsd(ev.startingPrice)} with ${rangeText(ev)} ${range}${charge}.`;
  return fitMeta(lead, [
    'Specs, pros, cons, rivals and what it costs to charge in your state.',
    'Specs, pros, cons and head-to-head rivals.',
    'Specs, pros and cons.',
  ]);
}

// ---------- blog posts ----------
function blogTitle(b) {
  return fitTitle([b.seoTitle, b.title]);
}

function blogDescription(b) {
  const firstPara = ((b.content || []).find((c) => c.type === 'paragraph') || {}).text || '';
  const plain = (t) => String(t || '').replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1');
  return fitMeta(b.metaDescription || b.snippet, splitSentences(plain(firstPara)).slice(0, 3));
}

module.exports = {
  TITLE_MAX,
  META_MIN,
  META_MAX,
  fitTitle,
  brandTitle,
  splitSentences,
  trimAtWord,
  fitMeta,
  hubTitle,
  hubDescription,
  LOGO_URL,
  organizationJsonLd,
  publisher,
  breadcrumbJsonLd,
  evTitle,
  evDescription,
  blogTitle,
  blogDescription,
};
